import { type HTMLAttributes, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Camera } from 'autogen/translation-keys/trans-website-imagealttext-icons';
import { ButtonWithIcon } from 'components/Button';
import Icon from 'components/Icon';
import dynamic from 'helpers/dynamic';
import { hashHistory } from 'modules/helpers/history';
import mediator from 'modules/mediator';
import Default from 'modules/modal/layouts/defaultLayout';
import type { ICameraWarningCallback } from 'modules/quicksearch/sharedInterfaces/ICameraWarningCallback';
import { GA4Tracking } from 'modules/tracking/GA4';
import { breakpoint, sizes } from 'variables';

const modalTheme = {
    maxHeight: '100%',
    maxWidth: '100%'
};

enum HashRoutes {
    VisualSearchPath = 'visualsearch',
    VisualSearchSkipIntroPath = 'visualsearchskipintro'
}

const VisualSearch = dynamic(() => import('modules/visualSearch/visualSearch'), { ssr: false });

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    ${breakpoint.up(sizes.lg)} {
        display: none;
    }
`;

export default function CameraButtonComponent({ className }: HTMLAttributes<HTMLDivElement>): JSX.Element {
    const [warningMessage, setWarningMessage] = useState<string | null>(null);
    const { t } = useTranslation();

    function cameraWarningCallback({ message }: ICameraWarningCallback): void {
        if (message) {
            setWarningMessage(message);
        }
    }

    async function openModal(): Promise<void> {
        const { default: createModal } = await import('modules/modal');
        const modal = createModal();
        const modalObjectTemplate = {
            content: <VisualSearch inModal modal={modal} warningCallback={cameraWarningCallback} />,
            open: true,
            layout: {
                component: (props) => <Default customTheme={modalTheme} {...props} />,
                header: null
            }
        };
        modal(modalObjectTemplate);
    }

    useEffect(() => {
        const unobserveHashHistoryChanges = hashHistory?.listen(({ location }) => {
            const { pathname } = location;

            if (pathname) {
                switch (pathname) {
                    case HashRoutes.VisualSearchSkipIntroPath:
                        localStorage.setItem('VSConditions', 'true');
                        break;
                    case HashRoutes.VisualSearchPath:
                        openModal();
                        break;
                    default:
                        break;
                }
            }
        });

        return (): any => {
            unobserveHashHistoryChanges?.(); // call to unlisten
        };
    }, []);

    return (
        <StyledButtonWithIcon
            className={className}
            icon={<Icon type="Camera" size={16} aria-label={t(Camera)} />}
            type="button"
            variant="ghostOnLight"
            id="js-VSButton"
            onClick={() => {
                if (warningMessage !== null) {
                    mediator.publish('growl', {
                        message: warningMessage
                    });

                    return;
                }

                GA4Tracking({
                    eventName: 'open_visual_search',
                    context: 'search',
                    category: 'ecommerce',
                    eventParams: [{ param_name: 'search_type', param_value: 'visual_search' }]
                });
                openModal();
            }}
        />
    );
}
